/*
 * SHIPS WITH sections/ergonix-product-hero-full.liquid (SENSITIVE-CONTRACTS #1).
 *
 * The per-colour gallery reads `window.productMediaByColor`, which that section
 * emits. The section is in pl_adapt.py CONTENT_ADAPTED, so it does NOT auto-sync
 * to the PL store while this file DOES - a solo push of this file lands on
 * ergonix.pl hours before the markup it depends on. That is exactly how the PL
 * quiz broke on 2026-06-12.
 *
 * Every per-colour path therefore falls back to the pre-2026-08 slot-0-only
 * behaviour when `productMediaByColor` is missing (legacySlotZeroSwap /
 * legacyMobileSlotZeroSwap). Do not remove those fallbacks, and push the two
 * files together.
 */

// The desktop #mainImage on the OmniPRO hero carries a srcset (640-2000w) so its 500-750 px box
// gets a right-sized file at 1x and >= 2x pixels on Retina. Every writer must then rewrite srcset with src, or the browser
// keeps showing the old srcset pick. No-op (plain .src) when the img has no srcset, so
// pages without it, and ergonix.pl before its section is ported, behave as before.
var ERX_MAIN_WIDTHS = [640, 800, 1000, 1240, 1600, 2000];
function erxMainSrcset(url) {
    if (!url || !/[?&]width=\d+/.test(url)) return '';
    return ERX_MAIN_WIDTHS.map(function (w) { return url.replace(/([?&])width=\d+/, '$1width=' + w) + ' ' + w + 'w'; }).join(', ');
}
// Desktop gallery thumbnails are 60 css px: 60 px files on 1x screens, 120 px on Retina via
// image-set(). Every JS writer goes through erxThumbBg so a colour switch keeps the 2x file.
// URLs without a width= param (legacy _60x60 names) fall back to a plain url().
var ERX_IMAGE_SET = (function () {
    try {
        if (CSS.supports('background-image', "image-set(url('x') 1x)")) return 'image-set';
        if (CSS.supports('background-image', "-webkit-image-set(url('x') 1x)")) return '-webkit-image-set';
    } catch (e) {}
    return '';
})();
function erxWidth(url, w) { return url.replace(/([?&])width=\d+/, '$1width=' + w); }
window.erxThumbBg = function (url) {
    if (!url) return '';
    if (!ERX_IMAGE_SET || !/[?&]width=\d+/.test(url)) return "url('" + url + "')";
    return ERX_IMAGE_SET + "(url('" + url + "') 1x, url('" + erxWidth(url, 120) + "') 2x)";
};
// The URL the thumbnail will actually show on this screen (for warm-up).
window.erxThumbFor = function (url) {
    return url && ERX_IMAGE_SET && (window.devicePixelRatio || 1) > 1 && /[?&]width=\d+/.test(url) ? erxWidth(url, 120) : url;
};
window.erxSetMainImage = function (img, url) {
    var set = img.hasAttribute('srcset') ? erxMainSrcset(url) : '';
    if (img.hasAttribute('srcset')) { if (set) img.srcset = set; else img.removeAttribute('srcset'); }
    img.src = url;
};
// Warms the exact candidate #mainImage will pick (same srcset + sizes), so swaps stay instant.
window.erxPreloadMainImage = function (url) {
    var main = document.getElementById('mainImage');
    var img = new Image();
    var set = main && main.hasAttribute('srcset') ? erxMainSrcset(url) : '';
    if (set) { img.sizes = main.getAttribute('sizes') || ''; img.srcset = set; }
    img.src = url;
    return img;
};

// Run fn once the page has finished loading and the browser is idle, so warm-up
// downloads never compete with what the visitor is looking at.
function erxWhenSettled(fn) {
    var go = function () {
        if ('requestIdleCallback' in window) requestIdleCallback(fn, { timeout: 4000 });
        else setTimeout(fn, 1000);
    };
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go, { once: true });
}

/*
 * Short dates in the delivery timeline ("Rugs. 9", "9. sept.").
 *
 * The month WORD comes from the locale (`window.cartStrings.months`), but the
 * ORDER is a locale rule of its own and used to be hardcoded as month-then-day
 * for everybody. That is English word order: it reads correctly in Lithuanian
 * ("Rugs. 9", cf. CLDR "rugsejo 9 d.") and wrongly in Latvian and Estonian,
 * which both put the day first - CLDR lv "9. sept.", et "9. sept". The LV and
 * EE storefronts shipped "Sept. 9" until 2026-09-09.
 *
 * Keep these in sync with the `months` key in locales/{lt,lv,et,...}.json.
 */
function ergxDateDayFirst() {
    const lang = (document.documentElement.lang || 'lt').slice(0, 2).toLowerCase();
    return lang === 'lv' || lang === 'et';
}

function ergxFormatShortDate(months, date) {
    const month = months[date.getMonth()];
    const day = date.getDate();
    return ergxDateDayFirst() ? `${day}. ${month}` : `${month} ${day}`;
}

function ergxFormatShortDateRange(months, start, end) {
    const startMonth = months[start.getMonth()];
    const endMonth = months[end.getMonth()];
    const startDay = start.getDate();
    const endDay = end.getDate();

    if (start.getMonth() === end.getMonth()) {
        return ergxDateDayFirst()
            ? `${startDay}.-${endDay}. ${startMonth}`
            : `${startMonth} ${startDay}-${endDay}`;
    }
    return ergxDateDayFirst()
        ? `${startDay}. ${startMonth} - ${endDay}. ${endMonth}`
        : `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
}

/*
 * Collapsible panels on a product page (the desktop ".info-content" accordion and the
 * mobile ".accordion-content" one) animate `max-height`. Both used to animate it towards
 * a FIXED cap - 300px for the desktop panels, 500px for the mobile ones - and a cap gets
 * two things wrong at once.
 *
 * Anything TALLER than the cap is simply cut off. The OmniARM specification panel is
 * 631px on desktop and 560px on a phone, so its dimensions drawing was sliced in half and
 * the assembly-manual PDF link underneath it could not be reached at all.
 *
 * Anything SHORTER finishes early and then sits still for the rest of the 0.3s. A 34px
 * panel reaches its full height 51ms in and holds for the remaining 249ms, while the
 * panel it replaced spends its first 78ms motionless and only then collapses - so
 * swapping two short panels reads as one snapping open and the other hanging behind it.
 * Two tall panels looked fine only because their content exceeded the cap, which made
 * their travel linear by accident.
 *
 * Driving max-height from each panel's own scrollHeight fixes both: every panel travels
 * its real distance across the full 0.3s, and nothing is clipped.
 *
 * The value is written INLINE deliberately. These selectors are declared in two built
 * stylesheets (ergonix-product-complete.css and ergonix-critical.css, the latter
 * generated from styles-src/ by scripts/minify-css.mjs), and an inline declaration beats
 * both. So neither sheet is touched, and the class caps stay behind as the fallback for a
 * page where this script fails to load.
 */
window.ErgonixCollapse = (function () {
    // 0.3s transition plus slack.
    var SETTLE_MS = 400;

    function reduced() {
        return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }

    function clearPending(el) {
        if (el._erxEnd) { el.removeEventListener('transitionend', el._erxEnd); el._erxEnd = null; }
        if (el._erxTimer) { clearTimeout(el._erxTimer); el._erxTimer = null; }
    }

    // transitionend does not fire in a background tab, nor when the start and end values
    // already match, so every settle is armed on a timer as well. Without it a panel
    // strands on an inline pixel value and the next image to load is clipped again.
    function settleAfter(el, settle) {
        clearPending(el);
        var handler = function (e) {
            // Ignore transitions bubbling up from children.
            if (e.target !== el || e.propertyName !== 'max-height') return;
            clearPending(el);
            settle();
        };
        el._erxEnd = handler;
        el.addEventListener('transitionend', handler);
        el._erxTimer = setTimeout(function () { clearPending(el); settle(); }, SETTLE_MS);
    }

    function open(el, activeClass) {
        clearPending(el);
        // Measure before the class goes on: 0 at rest, the current height on a re-click.
        var from = el.getBoundingClientRect().height;
        el.style.maxHeight = from + 'px';
        el.classList.add(activeClass);
        if (reduced()) { el.style.maxHeight = 'none'; return; }
        void el.offsetHeight; // flush, so the next value transitions instead of jumping
        el.style.maxHeight = el.scrollHeight + 'px';
        // Settle on `none`, never on the measured number: the specification panel holds a
        // lazily loaded image, so scrollHeight at click time can still be short.
        settleAfter(el, function () { el.style.maxHeight = 'none'; });
    }

    function close(el, activeClass) {
        clearPending(el);
        // Measure while the panel is still open - the class carries the open height.
        el.style.maxHeight = el.getBoundingClientRect().height + 'px';
        el.classList.remove(activeClass);
        if (reduced()) { el.style.maxHeight = ''; return; }
        void el.offsetHeight;
        el.style.maxHeight = '0px';
        settleAfter(el, function () { el.style.maxHeight = ''; });
    }

    // An image that lands while its panel is still travelling retargets that panel. A
    // panel that has already settled sits on max-height:none and needs nothing. `load`
    // does not bubble from an <img>, so this listens in the capture phase.
    document.addEventListener('load', function (e) {
        var img = e.target;
        if (!img || img.tagName !== 'IMG' || !img.closest) return;
        var panel = img.closest('.info-content, .accordion-content');
        if (panel && panel._erxEnd) panel.style.maxHeight = panel.scrollHeight + 'px';
    }, true);

    return { open: open, close: close };
})();

(function() {
    let currentImage = 0;
    let isTransitioning = false;
    let pendingImageIndex = null;
    let imageLoadGeneration = 0;

    function getMedia() {
        return window.allProductMedia || window.allProductImages || [];
    }

    function changeMedia(index) {
        // Get all thumbnails (support both new and old HTML structure)
        const thumbs = document.querySelectorAll('.ergonix-wrapper .thumbnails-container .thumbnail, .ergonix-wrapper .thumbnail-image');
        const totalCount = thumbs.length || getMedia().length;
        if (index < 0 || index >= totalCount) return;

        if (isTransitioning) {
            pendingImageIndex = index;
            return;
        }

        isTransitioning = true;
        currentImage = index;
        pendingImageIndex = null;

        const mainImage = document.getElementById('mainImage');
        const mainVideo = document.getElementById('mainVideo');
        const muteBtn = document.getElementById('desktopMuteBtn');

        // Determine if this is a video slide
        // Check thumbnail data-media-type first (new HTML), then media array (fallback)
        const thumb = thumbs[index];
        const mediaType = thumb ? thumb.getAttribute('data-media-type') : null;
        const videoSrc = thumb ? thumb.getAttribute('data-video-src') : null;
        const media = getMedia();
        const mediaItem = media[index];
        const isVideo = mediaType === 'video' || (mediaItem && (mediaItem.type === 'video' || mediaItem.videoSrc));
        const resolvedVideoSrc = videoSrc || (mediaItem && mediaItem.videoSrc);

        if (isVideo && resolvedVideoSrc) {
            // Show video, hide image
            if (mainImage) mainImage.style.display = 'none';
            if (mainVideo) {
                mainVideo.src = resolvedVideoSrc;
                mainVideo.style.display = 'block';
                mainVideo.currentTime = 0;
                mainVideo.play().catch(() => {});
            }
            if (muteBtn) muteBtn.style.display = 'flex';
            isTransitioning = false;
            if (pendingImageIndex !== null) {
                const next = pendingImageIndex;
                pendingImageIndex = null;
                changeMedia(next);
            }
        } else {
            // Show image, hide video
            if (mainVideo && mainVideo.src) {
                mainVideo.pause();
                mainVideo.style.display = 'none';
                mainVideo.removeAttribute('src');
                mainVideo.load();
            } else if (mainVideo) {
                mainVideo.style.display = 'none';
            }
            if (muteBtn) muteBtn.style.display = 'none';

            // PRIMARY: get image URL from the thumbnail's data-full-image attribute
            // This is the most reliable source since thumbnails match 1:1 with their index
            let fullUrl = thumb ? thumb.getAttribute('data-full-image') : null;
            // FALLBACK: try the media array
            if (!fullUrl && mediaItem) {
                fullUrl = mediaItem.src;
            }

            if (mainImage && fullUrl) {
                mainImage.style.display = 'block';
                imageLoadGeneration++;
                const gen = imageLoadGeneration;

                // Compare without protocol prefix to handle //url vs https://url
                const currentSrc = mainImage.src.replace(/^https?:/, '');
                const newSrc = fullUrl.replace(/^https?:/, '');

                if (currentSrc !== newSrc) {
                    const img = new Image();
                    const warmSet = mainImage.hasAttribute('srcset') ? erxMainSrcset(fullUrl) : '';
                    if (warmSet) { img.sizes = mainImage.getAttribute('sizes') || ''; img.srcset = warmSet; }
                    img.onload = img.onerror = () => {
                        if (gen === imageLoadGeneration) {
                            window.erxSetMainImage(mainImage, fullUrl);
                        }
                        isTransitioning = false;
                        if (pendingImageIndex !== null) {
                            const next = pendingImageIndex;
                            pendingImageIndex = null;
                            changeMedia(next);
                        }
                    };
                    img.src = fullUrl;
                } else {
                    isTransitioning = false;
                    if (pendingImageIndex !== null) {
                        const next = pendingImageIndex;
                        pendingImageIndex = null;
                        changeMedia(next);
                    }
                }
            } else {
                if (mainImage) mainImage.style.display = 'block';
                isTransitioning = false;
            }
        }

        updateThumbnails();
    }

    // Backward compat
    function changeImage(index) {
        changeMedia(index);
    }

    function toggleMute(event) {
        if (event) event.stopPropagation();
        const video = document.getElementById('mainVideo');
        if (!video) return;

        video.muted = !video.muted;

        const btn = document.getElementById('desktopMuteBtn');
        if (btn) {
            const volIcon = btn.querySelector('.icon-vol');
            const mutedIcon = btn.querySelector('.icon-muted');
            if (volIcon) volIcon.style.display = video.muted ? 'none' : 'inline';
            if (mutedIcon) mutedIcon.style.display = video.muted ? 'inline' : 'none';
        }
    }

    // Arrows wrap around: last image -> first, first -> last.
    function galleryCount() {
        const thumbs = document.querySelectorAll('.ergonix-wrapper .thumbnails-container .thumbnail, .ergonix-wrapper .thumbnail-image');
        return thumbs.length || getMedia().length;
    }

    function previousImage() {
        const total = galleryCount();
        if (total > 1) changeMedia((currentImage - 1 + total) % total);
    }

    function nextImage() {
        const total = galleryCount();
        if (total > 1) changeMedia((currentImage + 1) % total);
    }

    function updateThumbnails() {
        // Support both new (.thumbnail) and old (.thumbnail-image) selectors
        let thumbs = document.querySelectorAll('.ergonix-wrapper .thumbnails-container .thumbnail');
        if (thumbs.length === 0) {
            thumbs = document.querySelectorAll('.ergonix-wrapper .thumbnail-image');
        }
        thumbs.forEach((thumb, index) => {
            thumb.classList.toggle('active', index === currentImage);
        });

        // Also update video thumbnail if old structure
        const videoThumb = document.querySelector('.ergonix-wrapper .thumbnail-video');
        if (videoThumb) videoThumb.classList.remove('active');

        // Scroll active thumbnail into view
        if (thumbs[currentImage]) {
            thumbs[currentImage].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }

        // Update navigation buttons
        const media = getMedia();
        const totalCount = Math.max(media.length, thumbs.length);
        const prevBtn = document.querySelector('.ergonix-wrapper .image-nav:first-child');
        const nextBtn = document.querySelector('.ergonix-wrapper .image-nav:last-child');

        if (prevBtn) {
            prevBtn.classList.toggle('disabled', totalCount < 2);
        }
        if (nextBtn) {
            nextBtn.classList.toggle('disabled', totalCount < 2);
        }
    }

    // Color translations via locale strings from layout
    // Colour translations from the layout's locale strings. Built ON EVERY CALL, not
    // once at parse time: this file is loaded in <head> with no defer, while
    // window.cartStrings is an inline script much further down the page, so at parse
    // time it is ALWAYS undefined and the map collapsed to its English fallback. The
    // visible effect on ergonix.lt was picking Grey relabelling the chair "Grey"
    // instead of "Pilka" (2026-08-12). Read late, not early.
    function colorStringsMap() {
        var cs = window.cartStrings || {};
        var grey = cs.colorGrey || 'Grey';
        var black = cs.colorBlack || 'Black';
        return { 'Grey': grey, 'Gray': grey, 'Pilka': grey, 'Szary': grey,
                 'Black': black, 'Juoda': black, 'Czarny': black };
    }

    function translateColorToLithuanian(color) {
        return colorStringsMap()[color] || color;
    }

    function selectColor(color) {
        // Translate and update label
        const colorLabel = document.querySelector('.ergonix-wrapper .color-label strong');
        if (colorLabel) {
            colorLabel.textContent = translateColorToLithuanian(color);
        }

        // NEW: Variant mapping (will be populated dynamically)
        const variantMap = window.productVariants || {
            'Black': '52380192702797',
            'Grey': '52380192735565'
        };

        const variantId = variantMap[color];

        // Update hidden form input
        const input = document.getElementById('variant-id-input');
        if (input) {
            input.value = variantId;
        }

        // Update global state
        window.currentSelectedVariant = variantId;

        // Update price display
        if (window.cart && window.cart.updatePriceDisplay) {
            window.cart.updatePriceDisplay(variantId);
        }

        // Update desktop sticky cart (like mobile does)
        if (typeof updateDesktopStickyCartPrice === 'function') {
            setTimeout(updateDesktopStickyCartPrice, 100);
        }

        // Visual feedback on color options
        document.querySelectorAll('.color-option').forEach(opt => {
            opt.classList.remove('active');
        });
        // Find the clicked color option and add active class
        const colorOptions = document.querySelectorAll('.color-option');
        colorOptions.forEach(option => {
            if (option.dataset.color === color) {
                option.classList.add('active');
            }
        });

        // Update URL with ?variant= (without page reload). The variant ID, NOT the
        // colour name: Shopify resolves an id server-side, so a refresh or a shared
        // link renders the right colour immediately. Writing the name meant the
        // server always rendered the default (black) and this script swapped to grey
        // after load - the visible flash of the wrong chair on every reload.
        if (variantId) {
            const url = new URL(window.location);
            url.searchParams.set('variant', variantId);
            window.history.replaceState({}, '', url);
        }

        // NEW: Update images based on selected variant
        updateImagesForVariant(color);

        // Update timeline dates based on selected variant
        updateTimelineForVariant(color);

        // Keep the sticky bar's dot, colour name, thumbnail and price in step.
        // Defined in the second IIFE next to the rest of the sticky-cart code, so
        // it is reached through window, like desktopSelectColor / ergonixApplyOOS.
        if (typeof window.ergonixSyncStickyCart === 'function') window.ergonixSyncStickyCart(color);

        // Out-of-stock UI: toggle notification card + disable CTA when sold out
        applyOutOfStockState(color, variantId, '#oos-chair-desktop', '#ergonix-chair-cta');
    }

    /**
     * Render OOS card + disable add-to-cart for the given color/variant.
     * Used by both the desktop and mobile color selection paths.
     *
     * Uses inventory_quantity (not variant.available) because the store
     * runs Shopify's "continue selling when out of stock" policy — so
     * .available stays true at 0 stock and would mask the sold-out state.
     */
    function applyOutOfStockState(color, variantId, cardSelector, ctaSelector) {
        const data = (window.productVariantsData || []).find(v => v.option1 === color);
        const card = document.querySelector(cardSelector);
        const cta = document.querySelector(ctaSelector);
        // 'continue' policy = merchant allows selling at 0 stock; never OOS.
        const isSoldOut = data
            ? (!!data.inventory_management && (data.inventory_quantity || 0) <= 0 && data.inventory_policy !== 'continue')
            : false;

        if (window.ErgonixOOS && card) {
            if (isSoldOut) {
                window.ErgonixOOS.show(card, {
                    variantId: variantId,
                    productId: (window.productData && window.productData.id) || ''
                });
            } else {
                window.ErgonixOOS.hide(card);
            }
        }
        if (cta) {
            cta.disabled = isSoldOut;
            cta.classList.toggle('order-btn--soldout', isSoldOut);
            cta.classList.toggle('mobile-order-button--soldout', isSoldOut);
        }
        // Sold out: hide the sticky add-to-cart bars entirely, they are pure
        // add-to-cart shortcuts, so the on-page sold-out card stays the single
        // call to action (sticky-cart--soldout is styled in ergonix-product-complete.css).
        ['desktop-sticky-cart', 'mobile-sticky-cart'].forEach(function (id) {
            const bar = document.getElementById(id);
            if (bar) bar.classList.toggle('sticky-cart--soldout', isSoldOut);
        });
    }

    function escAttr(value) {
        return String(value == null ? '' : value)
            .replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
    }

    /**
     * The full media list for a colour: the variant's colour render at slot 0,
     * then that colour's gallery set. Mirrors how the section renders the rail.
     *
     * Returns null when the page has no per-colour data, which is the case on
     * any PDP whose section has not been ported (the desk, OmniARM) AND on the
     * PL store in the window between the JS syncing and the section being
     * hand-ported - sections/ergonix-product-hero-full.liquid is in pl_adapt.py
     * CONTENT_ADAPTED and never auto-syncs. Callers MUST fall back to
     * legacySlotZeroSwap in that case or the PL rail gets wiped to one slide.
     */
    function railItemsFor(color) {
        const variantData = window.productVariantsData
            ? window.productVariantsData.find(v => v.option1 === color) : null;
        const set = window.productMediaByColor && window.productMediaByColor[color];
        if (!variantData || !set || !set.length) return null;

        const list = [];
        const fi = variantData.featured_image;
        if (fi) {
            list.push({
                type: 'image', src: fi.src, videoSrc: null, thumb: fi.thumb,
                mobileSrc: fi.mobileSrc, mobileThumb: fi.mobileThumb, alt: fi.alt || ''
            });
        }
        return list.concat(set);
    }

    /**
     * Emits markup identical to the rail in sections/ergonix-product-hero-full.liquid
     * (including role="button" tabindex="0") - keep the two in sync.
     */
    function buildDesktopRail(list, activeIndex) {
        const container = document.querySelector('.ergonix-wrapper .thumbnails-container');
        if (!container) return false;
        const active = activeIndex || 0;
        container.innerHTML = list.map(function(m, i) {
            const video = m.type === 'video'
                ? ' data-media-type="video" data-video-src="' + escAttr(m.videoSrc) + '"' : '';
            return '<div class="thumbnail thumbnail-image' + (i === active ? ' active' : '') + '"' +
                   ' role="button" tabindex="0"' +
                   ' style="background-image: ' + escAttr(window.erxThumbBg(m.thumb)) + ';"' +
                   ' data-full-image="' + escAttr(m.src) + '"' + video +
                   ' onclick="ErgonixProduct.changeImage(' + i + ')"></div>';
        }).join('');
        return true;
    }

    // Pre-2026-08 behaviour: swap slot 0 only. Kept as the fallback path.
    function legacySlotZeroSwap(color) {
        const variantData = window.productVariantsData ? window.productVariantsData.find(v => v.option1 === color) : null;
        if (!variantData || !variantData.featured_image) return;

        const fi = variantData.featured_image;

        // Update first thumbnail (support both old and new selectors)
        let thumbnails = document.querySelectorAll('.ergonix-wrapper .thumbnails-container .thumbnail');
        if (thumbnails.length === 0) {
            thumbnails = document.querySelectorAll('.ergonix-wrapper .thumbnail-image');
        }
        if (thumbnails[0]) {
            thumbnails[0].style.backgroundImage = window.erxThumbBg(fi.thumb);
            thumbnails[0].setAttribute('data-full-image', fi.src);
        }

        // Also update allProductMedia[0] if it's an image
        const media = getMedia();
        if (media[0] && media[0].type === 'image') {
            media[0].src = fi.src;
            media[0].thumb = fi.thumb;
        }

        currentImage = 0;
        changeMedia(0);
    }

    function updateImagesForVariant(color) {
        const list = railItemsFor(color);
        if (!list) return legacySlotZeroSwap(color);

        // STAY ON THE SAME SCENE. Every colour set is the same 8 scenes in the same
        // order, so index N is the same photograph in the other colour - which is
        // exactly the comparison the shopper is making when they click the swatch.
        // Resetting to slot 0 (inherited from when only slot 0 ever changed) threw
        // them back to the plain white-background render every time.
        const keep = Math.min(Math.max(currentImage, 0), list.length - 1);

        window.allProductMedia = list;
        window.allProductImages = list.filter(function(m) { return m.type === 'image'; });
        if (!buildDesktopRail(list, keep)) return legacySlotZeroSwap(color);

        // changeMedia derives its count from the rebuilt DOM and reads
        // data-full-image off the new thumb, so it needs nothing else.
        currentImage = keep;
        changeMedia(keep);
    }

    /**
     * The Black variant has a longer delivery window. Shopify's option1
     * value is localized per market (Black/Czarny/Juoda) so we match all
     * spellings — strict equality on 'Black' breaks on PL/LT.
     */
    function isLongDeliveryColor(color) {
        if (!color) return false;
        var lower = String(color).toLowerCase();
        return lower === 'black' || lower === 'czarny' || lower === 'juoda' || lower === 'juodas';
    }

    // Lithuanian and Polish public holidays. Mirrors snippets/ergonix-delivery-dates.liquid;
    // refresh both lists annually. Dates that fall on Sat/Sun are omitted (weekend skip covers them).
    const ERGONIX_HOLIDAYS = {
        lt: new Set([
            '2026-01-01', '2026-02-16', '2026-03-11', '2026-04-06', '2026-05-01',
            '2026-06-24', '2026-07-06', '2026-11-02', '2026-12-24', '2026-12-25',
            '2027-01-01', '2027-02-16', '2027-03-11', '2027-03-29',
            '2027-06-24', '2027-07-06', '2027-11-01', '2027-11-02', '2027-12-24'
        ]),
        pl: new Set([
            '2026-01-01', '2026-01-06', '2026-04-06', '2026-05-01', '2026-06-04',
            '2026-11-11', '2026-12-24', '2026-12-25',
            '2027-01-01', '2027-01-06', '2027-03-29', '2027-05-03', '2027-05-27',
            '2027-11-01', '2027-11-11', '2027-12-24'
        ])
    };

    function isoDate(d) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }

    function nextBusinessDays(count) {
        const locale = (document.documentElement.lang || 'lt').toLowerCase();
        const holidays = ERGONIX_HOLIDAYS[locale] || ERGONIX_HOLIDAYS.lt;
        const result = [];
        const cursor = new Date();
        cursor.setHours(12, 0, 0, 0);
        let safety = 60;
        while (result.length < count && safety-- > 0) {
            cursor.setDate(cursor.getDate() + 1);
            const wday = cursor.getDay();
            if (wday === 0 || wday === 6) continue;
            if (holidays.has(isoDate(cursor))) continue;
            result.push(new Date(cursor));
        }
        return result;
    }

    function updateTimelineForVariant(color) {
        const months = (window.cartStrings && window.cartStrings.months) || ['Saus.', 'Vas.', 'Kov.', 'Bal.', 'Geg.', 'Birž.', 'Liep.', 'Rugp.', 'Rugs.', 'Spal.', 'Lapkr.', 'Gruod.'];

        let shipText, deliveryText;

        // Black-as-long-delivery override disabled — all colors now share the
        // default LT/PL business-day window mirrored from ergonix-delivery-dates.
        // if (isLongDeliveryColor(color)) {
        //     shipText = months[4] + ' 9';
        //     deliveryText = months[4] + ' 11';
        // } else {
        const days = nextBusinessDays(4);
        const shipDate = days[0];
        const deliveryStart = days[1];
        const deliveryEnd = days[3];

        shipText = ergxFormatShortDate(months, shipDate);
        deliveryText = ergxFormatShortDateRange(months, deliveryStart, deliveryEnd);
        // }

        document.querySelectorAll('[data-timeline-type="ship-date"]').forEach(el => {
            el.textContent = shipText;
        });

        document.querySelectorAll('[data-timeline-type="delivery-date"]').forEach(el => {
            el.textContent = deliveryText;
        });

        // Indicator span is now commented out in the Liquid template, but keep
        // the toggle so any cached/preloaded markup still gets hidden.
        document.querySelectorAll('[data-black-delay-indicator]').forEach(el => {
            el.hidden = true;
        });
    }

    // Expose globally so mobile IIFE + other handlers can access them
    window.updateTimelineForVariant = updateTimelineForVariant;
    window.isLongDeliveryColor = isLongDeliveryColor;
    window.ergonixApplyOOS = applyOutOfStockState;

    const INFO_ICON_PLUS = '<path d="M10 5V15" stroke="#1A1A1A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M5 10H15" stroke="#1A1A1A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>';
    const INFO_ICON_MINUS = '<path d="M5 10H15" stroke="#1A1A1A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>';

    // Heights come from window.ErgonixCollapse (top of this file), which measures each
    // panel instead of animating it towards a fixed cap. See the comment there.
    function toggleSection(header) {
        const content = header.nextElementSibling;
        const icon = header.querySelector('.info-toggle svg');
        if (!content || !icon) return;

        // Scoped to this card. The old selector closed every .info-content in the document,
        // which is harmless while a template renders one card and a trap the day one
        // renders two.
        const card = (header.closest && header.closest('.info-card')) || document;

        if (content.classList.contains('active')) {
            window.ErgonixCollapse.close(content, 'active');
            icon.innerHTML = INFO_ICON_PLUS;
            return;
        }

        // Close the siblings, then open this one.
        card.querySelectorAll('.info-content.active').forEach(c => {
            if (c !== content) window.ErgonixCollapse.close(c, 'active');
        });
        card.querySelectorAll('.info-header').forEach(h => {
            if (h === header) return;
            const i = h.querySelector('.info-toggle svg');
            if (i) i.innerHTML = INFO_ICON_PLUS;
        });

        window.ErgonixCollapse.open(content, 'active');
        icon.innerHTML = INFO_ICON_MINUS;
    }

    // Make functions available through namespace to avoid global pollution
    // Use Object.assign to MERGE with existing methods (inline scripts in sections
    // add toggleSpecifications, toggleFAQ etc. before this deferred script runs)
    window.ErgonixProduct = Object.assign(window.ErgonixProduct || {}, {
        changeMedia: changeMedia,
        changeImage: changeImage,
        previousImage: previousImage,
        nextImage: nextImage,
        selectColor: selectColor,
        toggleSection: toggleSection,
        toggleMute: toggleMute,
        toggleSound: toggleMute,
        playVideo: function() {},
        toggleVideo: function() {}
    });

    // Expose selectColor globally for desktop sticky cart to use (like mobile does)
    window.desktopSelectColor = selectColor;

    // The desktop thumbnails are divs with an inline onclick, so a mouse is the only
    // way to reach them. Delegated (not per-element) because buildDesktopRail replaces
    // the whole rail on every colour change.
    document.addEventListener('keydown', function(e) {
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        const target = e.target;
        if (!target || !target.closest) return;
        const thumb = target.closest('.ergonix-wrapper .thumbnails-container .thumbnail, .ergonix-wrapper .thumbnail-image');
        if (!thumb) return;
        e.preventDefault();
        thumb.click();
    });

    // Mobile functions are handled by the second IIFE below

    // Preload all variant images for instant switching
    function preloadAllVariantImages() {
        if (!window.productVariantsData) return;

        console.log('Preloading all variant images for instant switching...');

        window.productVariantsData.forEach(function(variant) {
            if (variant.images && variant.images.length > 0) {
                variant.images.forEach(function(imageData) {
                    // Preload full-size image
                    window.erxPreloadMainImage(imageData.src);

                    // Preload thumbnail
                    const thumb = new Image();
                    thumb.src = window.erxThumbFor(imageData.thumb);
                });
                console.log(`Preloaded ${variant.images.length} images for ${variant.option1} variant`);
            }
            // Variant colour renders are filtered OUT of variant.images (the
            // shared set) - preload each variant's own featured image too, or
            // the first swatch switch fetches the render on demand.
            if (variant.featured_image) {
                if (variant.featured_image.src) { window.erxPreloadMainImage(variant.featured_image.src); }
                if (variant.featured_image.thumb) { new Image().src = window.erxThumbFor(variant.featured_image.thumb); }
            }
        });
    }

    // Initialize
    document.addEventListener('DOMContentLoaded', function() {

        // Check for ?variant= URL parameter
        const urlParams = new URLSearchParams(window.location.search);
        const variantFromUrl = urlParams.get('variant');
        let selectedColor = null;

        // If variant parameter exists and is valid color name
        if (variantFromUrl && ['Black', 'Grey', 'Gray'].includes(variantFromUrl)) {
            selectedColor = variantFromUrl === 'Gray' ? 'Grey' : variantFromUrl; // Normalize Gray to Grey
            console.log('Variant from URL:', selectedColor);

            // Auto-select the color from URL
            selectColor(selectedColor);

            // Update active color swatch visually
            document.querySelectorAll('.ergonix-wrapper .color-option').forEach(option => {
                if (option.dataset.color === selectedColor) {
                    option.classList.add('active');
                } else {
                    option.classList.remove('active');
                }
            });
        } else if (variantFromUrl && window.productVariantsData) {
            // Variant parameter is an ID - look up the exact variant
            const urlVariant = window.productVariantsData.find(v => v.id === variantFromUrl);
            // Shopify already rendered this variant server-side, so touching the
            // images here would only repaint what is already correct - and that
            // repaint IS the flash we are trying to remove. Do nothing.
            const serverColor = document.querySelector('.ergonix-wrapper .color-option.active')?.dataset.color;
            if (urlVariant && urlVariant.option1 === serverColor) {
                console.log('URL variant matches the server-rendered colour - nothing to do');
            } else if (urlVariant) {
                console.log('Variant ID from URL, resolved:', urlVariant.option1, urlVariant.option2);
                // Find the correct image: try featured_image, then alt-text match
                let variantImg = urlVariant.featured_image;
                if (!variantImg && window.allProductMedia) {
                    const comboKey = urlVariant.option1 + ' ' + urlVariant.option2;
                    variantImg = window.allProductMedia.find(img => img.alt === comboKey);
                }
                if (variantImg) {
                    const mainImg = document.getElementById('mainImage');
                    if (mainImg) window.erxSetMainImage(mainImg, variantImg.src);
                    // Update first thumbnail to match
                    const firstThumb = document.querySelector('.ergonix-wrapper .thumbnail:first-child');
                    if (firstThumb) {
                        firstThumb.style.backgroundImage = window.erxThumbBg(variantImg.thumb);
                        firstThumb.setAttribute('data-full-image', variantImg.src);
                    }
                } else {
                    // No variant-specific image found, use color-based init
                    updateImagesForVariant(urlVariant.option1);
                }
            }
        } else {
            // No URL parameter - HTML already renders the correct default image.
            // Skip updateImagesForVariant to prevent image flash/reload.
            console.log('No URL variant param - skipping desktop image update (HTML already correct)');
        }

        updateThumbnails();

        // Preload all variant images after page load for instant switching. Desktop
        // sizes only on desktop: phones run preloadAllMobileVariantImages instead.
        if (window.innerWidth > 768) erxWhenSettled(preloadAllVariantImages);

        // Initial OOS state (covers default color when no ?variant= param)
        var activeSwatch = document.querySelector('.ergonix-wrapper .color-option.active');
        if (activeSwatch) {
            var initColor = activeSwatch.dataset.color;
            var initVariantId = activeSwatch.dataset.variantId || (window.productVariants && window.productVariants[initColor]);
            applyOutOfStockState(initColor, initVariantId, '#oos-chair-desktop', '#ergonix-chair-cta');
        }
    });
})();

// Ridge-style Mobile Slider - track-based translateX with touch drag
(function() {
    let currentIndex = 0;
    let totalSlides = 0;

    // Touch / drag state
    let isDragging = false;
    let startX = 0;
    let startY = 0;
    let dragOffset = 0;        // px offset from finger drag
    let trackWidth = 0;
    let directionLocked = false;
    let isHorizontal = false;

    // Lazy-load state
    let slidesLoaded = new Set([0]);
    let galleryInitialized = false;

    function getTrack() { return document.getElementById('mobileSliderTrack'); }
    function getSlides() { return document.querySelectorAll('.mobile-slider__slide'); }
    function getDots() { return document.querySelectorAll('.mobile-slider__dot'); }

    // ---- Initialization ----
    function initializeMobileGallery() {
        const slider = document.getElementById('mobileSlider');
        if (!slider) return;

        const slides = getSlides();
        totalSlides = slides.length;
        if (totalSlides === 0) return;

        // Guard: only attach touch handlers once (resize handler can re-call this)
        if (!galleryInitialized) {
            slider.addEventListener('touchstart', onTouchStart, { passive: true });
            slider.addEventListener('touchmove', onTouchMove, { passive: false });
            slider.addEventListener('touchend', onTouchEnd, { passive: true });
            galleryInitialized = true;
        }

        // Keyboard
        document.addEventListener('keydown', function(e) {
            if (window.innerWidth > 768) return;
            if (e.key === 'ArrowLeft') { e.preventDefault(); goToSlide(currentIndex - 1); }
            if (e.key === 'ArrowRight') { e.preventDefault(); goToSlide(currentIndex + 1); }
        });

        // Preload slide 1 (slide 0 is already eager)
        preloadSlide(1);

        // Initialize slider position and load video on slide 0 if needed
        goToSlide(0);
    }

    // ---- Track position ----
    function setTrackPosition(px, animate) {
        const track = getTrack();
        if (!track) return;
        if (animate) {
            track.classList.add('is-animating');
        } else {
            track.classList.remove('is-animating');
        }
        track.style.transform = 'translateX(' + px + 'px)';
    }

    function getBaseOffset() {
        const slider = document.getElementById('mobileSlider');
        trackWidth = slider ? slider.offsetWidth : window.innerWidth;
        return -currentIndex * trackWidth;
    }

    // ---- Touch handlers ----
    function onTouchStart(e) {
        if (e.touches.length !== 1) return;
        const track = getTrack();
        if (track) track.classList.remove('is-animating');

        const slider = document.getElementById('mobileSlider');
        trackWidth = slider ? slider.offsetWidth : window.innerWidth;

        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        dragOffset = 0;
        isDragging = true;
        directionLocked = false;
        isHorizontal = false;
    }

    function onTouchMove(e) {
        if (!isDragging) return;
        // A second finger means a pinch-zoom gesture: cancel the swipe and snap
        // back so zooming never changes the slide.
        if (e.touches.length !== 1) {
            isDragging = false;
            setTrackPosition(getBaseOffset(), true);
            return;
        }

        const dx = e.touches[0].clientX - startX;
        const dy = e.touches[0].clientY - startY;

        // Lock direction after 10px of movement
        if (!directionLocked && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
            directionLocked = true;
            isHorizontal = Math.abs(dx) > Math.abs(dy);
            if (!isHorizontal) {
                isDragging = false;
                return;
            }
        }

        if (!isHorizontal) return;
        e.preventDefault();

        // Elastic rubber-band at edges
        let offset = dx;
        if ((currentIndex === 0 && dx > 0) || (currentIndex === totalSlides - 1 && dx < 0)) {
            offset = dx * 0.3;
        }

        dragOffset = offset;
        setTrackPosition(getBaseOffset() + dragOffset, false);
    }

    function onTouchEnd() {
        if (!isDragging) return;
        isDragging = false;

        if (!isHorizontal) {
            // Snap back if not horizontal
            setTrackPosition(getBaseOffset(), true);
            return;
        }

        const threshold = trackWidth * 0.2;
        let target = currentIndex;

        if (dragOffset < -threshold && currentIndex < totalSlides - 1) {
            target = currentIndex + 1;
        } else if (dragOffset > threshold && currentIndex > 0) {
            target = currentIndex - 1;
        }

        dragOffset = 0;
        goToSlide(target);
    }

    // ---- Navigation ----
    function goToSlide(index) {
        if (totalSlides === 0) return; // Not initialized yet
        if (index < 0) index = 0;
        if (index >= totalSlides) index = totalSlides - 1;

        const prevIndex = currentIndex;
        currentIndex = index;

        // Recalc width in case of orientation change
        const slider = document.getElementById('mobileSlider');
        trackWidth = slider ? slider.offsetWidth : window.innerWidth;

        setTrackPosition(getBaseOffset(), true);
        updateDots();

        // Lazy-load current and adjacent slides
        preloadSlide(currentIndex);
        preloadSlide(currentIndex + 1);
        preloadSlide(currentIndex - 1);

        // Video handling: pause previous, play current
        handleVideoTransition(prevIndex, currentIndex);
    }

    // Arrows wrap around; swipes still stop at the ends (goToSlide clamps).
    function nextImage() { if (totalSlides > 1) goToSlide((currentIndex + 1) % totalSlides); }
    function previousImage() { if (totalSlides > 1) goToSlide((currentIndex - 1 + totalSlides) % totalSlides); }
    function selectImage(index) { goToSlide(index); }
    function selectMedia(index) { goToSlide(index); }

    // ---- Dots ----
    function updateDots() {
        const dots = getDots();
        dots.forEach(function(dot, i) {
            dot.classList.toggle('active', i === currentIndex);
        });
        // Sync the thumbnail strip (if present). Bring the active thumb into view
        // only when it's off-screen, scrolling to the NEAREST edge - never centered,
        // so the strip starts from the beginning on slide 0.
        const thumbs = document.querySelectorAll('.mobile-slider__thumb');
        if (thumbs.length) {
            const strip = document.getElementById('mobileSliderThumbs');
            thumbs.forEach(function(thumb, i) {
                const on = i === currentIndex;
                thumb.classList.toggle('active', on);
                if (on && strip) {
                    const tLeft = thumb.offsetLeft;
                    const tRight = tLeft + thumb.offsetWidth;
                    const vLeft = strip.scrollLeft;
                    const vRight = vLeft + strip.clientWidth;
                    if (tLeft < vLeft + 16) {
                        strip.scrollTo({ left: Math.max(0, tLeft - 16), behavior: 'smooth' });
                    } else if (tRight > vRight - 16) {
                        strip.scrollTo({ left: tRight - strip.clientWidth + 16, behavior: 'smooth' });
                    }
                }
            });
        }
    }

    // ---- Lazy load ----
    function preloadSlide(index) {
        if (index < 0 || index >= totalSlides || slidesLoaded.has(index)) return;
        slidesLoaded.add(index);

        const slide = getSlides()[index];
        if (!slide) return;

        const img = slide.querySelector('.mobile-slider__image');
        if (img) {
            const fullSrc = img.getAttribute('data-full-src');
            const fullSrcset = img.getAttribute('data-full-srcset');
            if (fullSrc && img.src !== fullSrc) {
                img.src = fullSrc;
                if (fullSrcset) img.srcset = fullSrcset;
            }
        }
    }

    // ---- Video handling ----
    function handleVideoTransition(prevIdx, newIdx) {
        // Pause video on previous slide
        if (prevIdx !== newIdx) {
            const prevSlide = getSlides()[prevIdx];
            if (prevSlide) {
                const prevVideo = prevSlide.querySelector('.mobile-slider__video');
                if (prevVideo) { prevVideo.pause(); }
            }
        }

        // Autoplay video on current slide
        const curSlide = getSlides()[newIdx];
        if (curSlide && curSlide.getAttribute('data-media-type') === 'video') {
            const video = curSlide.querySelector('.mobile-slider__video');
            if (video) {
                const src = video.getAttribute('data-src');
                if (src && !video.src) { video.src = src; }
                video.currentTime = 0;
                video.play().catch(function() {});
            }
        }
    }

    function mobileToggleMute(event, slideIndex) {
        if (event) event.stopPropagation();
        var slide;
        if (typeof slideIndex === 'number') {
            slide = getSlides()[slideIndex];
        } else {
            slide = getSlides()[currentIndex];
        }
        if (!slide) return;

        var video = slide.querySelector('.mobile-slider__video');
        if (!video) return;

        video.muted = !video.muted;

        var btn = slide.querySelector('.mobile-slider__mute-btn');
        if (btn) {
            var volIcon = btn.querySelector('.icon-vol');
            var mutedIcon = btn.querySelector('.icon-muted');
            if (volIcon) volIcon.style.display = video.muted ? 'none' : 'inline';
            if (mutedIcon) mutedIcon.style.display = video.muted ? 'inline' : 'none';
        }
    }
    
    
    // Mobile color translations via locale strings
    // Colour translations from the layout's locale strings. Built ON EVERY CALL, not
    // once at parse time: this file is loaded in <head> with no defer, while
    // window.cartStrings is an inline script much further down the page, so at parse
    // time it is ALWAYS undefined and the map collapsed to its English fallback. The
    // visible effect on ergonix.lt was picking Grey relabelling the chair "Grey"
    // instead of "Pilka" (2026-08-12). Read late, not early.
    function colorStringsMap() {
        var cs = window.cartStrings || {};
        var grey = cs.colorGrey || 'Grey';
        var black = cs.colorBlack || 'Black';
        return { 'Grey': grey, 'Gray': grey, 'Pilka': grey, 'Szary': grey,
                 'Black': black, 'Juoda': black, 'Czarny': black };
    }

    function mobileTranslateColor(color) {
        return colorStringsMap()[color] || color;
    }

    function mobileSelectColor(color) {
        // Update mobile color selection with Lithuanian translation
        const colorLabel = document.querySelector('.ergonix-wrapper .mobile-color-label strong');
        if (colorLabel) {
            colorLabel.textContent = mobileTranslateColor(color);
        }

        // Variant mapping - same as desktop (will be populated dynamically)
        const variantMap = window.productVariants || {
            'Black': '52380192702797',
            'Grey': '52380192735565'
        };

        const variantId = variantMap[color];

        // Update hidden form input
        const input = document.getElementById('variant-id-input');
        if (input) {
            input.value = variantId;
        }

        // Update global state
        window.currentSelectedVariant = variantId;

        // Update price display
        if (window.cart && window.cart.updatePriceDisplay) {
            window.cart.updatePriceDisplay(variantId);
        }

        // Update sticky cart price (if sticky cart exists)
        if (typeof updateStickyCartPrice === 'function') {
            setTimeout(updateStickyCartPrice, 100); // Small delay to let price update finish
        }

        // Update color options visual state
        const colorOptions = document.querySelectorAll('.ergonix-wrapper .mobile-color-option');
        colorOptions.forEach(option => {
            option.classList.remove('active');
        });

        // Find and activate the selected color
        colorOptions.forEach(option => {
            if (option.dataset.color === color) {
                option.classList.add('active');
            }
        });

        // NEW: Update images based on selected variant
        updateMobileImagesForVariant(color);

        // Update chair color preview image
        var chairPreview = document.getElementById('mobileChairPreviewImg');
        if (chairPreview) {
            var vData = window.productVariantsData ? window.productVariantsData.find(function(v) { return v.option1 === color; }) : null;
            if (vData && vData.featured_image) {
                chairPreview.src = vData.featured_image.mobileSrc || vData.featured_image.src;
            }
        }

        // Keep the URL on the selected variant ID so a reload renders this colour
        // server-side instead of flashing the default first (see selectColor).
        if (variantId) {
            const murl = new URL(window.location);
            murl.searchParams.set('variant', variantId);
            window.history.replaceState({}, '', murl);
        }

        // Update timeline dates based on selected variant
        window.updateTimelineForVariant(color);

        // Out-of-stock UI: notification card + disabled CTA when sold out
        if (window.ergonixApplyOOS) {
            window.ergonixApplyOOS(color, variantId, '#oos-chair-mobile', '#ergonix-chair-mobile-cta');
        }
    }

    function escAttrM(value) {
        return String(value == null ? '' : value)
            .replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
    }

    /**
     * Rebuild the track, dots and thumb strip from a per-colour media list.
     * Emits the same markup as sections/ergonix-product-hero-full.liquid - keep in sync.
     * Returns false (so the caller can fall back) when the DOM isn't the multi-slide
     * slider, or when the list contains a video: the slider markup here is
     * image-only, and rebuilding a video slide as an <img> would make it unplayable.
     */
    function buildMobileRail(list, activeIndex) {
        var track = getTrack();
        if (!track) return false;
        for (var v = 0; v < list.length; v++) {
            if (list[v].type === 'video') return false;
        }
        var active = Math.min(Math.max(activeIndex || 0, 0), list.length - 1);
        var dots = document.getElementById('mobileSliderDots');
        var strip = document.getElementById('mobileSliderThumbs');
        // productTitle is emitted next to productHandle/productId by the section, so the
        // rebuilt slides keep the same alt text the server rendered ("<title> - N").
        var title = window.productTitle || (window.productData && window.productData.title) || '';

        track.innerHTML = list.map(function(m, i) {
            var full = m.mobileSrc || m.src;
            var srcset = escAttrM(full) + ' 600w';
            return '<div class="mobile-slider__slide" data-slide-index="' + i + '" data-media-type="image">' +
                   '<img src="' + escAttrM(i === active ? full : (m.mobileThumb || full)) + '"' +
                   ' data-full-src="' + escAttrM(full) + '"' +
                   ' data-full-srcset="' + srcset + '"' +
                   (i === active ? ' srcset="' + srcset + '" fetchpriority="high"' : '') +
                   ' sizes="100vw" alt="' + escAttrM(title) + ' - ' + (i + 1) + '"' +
                   ' loading="' + (i === active ? 'eager' : 'lazy') + '"' +
                   ' class="mobile-slider__image" draggable="false"></div>';
        }).join('');

        if (dots) {
            dots.innerHTML = list.map(function(m, i) {
                return '<button class="mobile-slider__dot' + (i === active ? ' active' : '') + '"' +
                       ' data-index="' + i + '" onclick="ErgonixMobile.goToSlide(' + i + ')"' +
                       ' aria-label="Slide ' + (i + 1) + '"></button>';
            }).join('');
        }

        if (strip) {
            strip.innerHTML = list.map(function(m, i) {
                return '<button type="button" class="mobile-slider__thumb' + (i === active ? ' active' : '') + '"' +
                       ' data-index="' + i + '" onclick="ErgonixMobile.goToSlide(' + i + ')"' +
                       ' aria-label="' + escAttrM(title) + ' ' + (i + 1) + '">' +
                       '<img src="' + escAttrM(m.mobileThumb || m.thumb) + '" alt="" loading="lazy" draggable="false"></button>';
            }).join('');
        }

        // Reset slider state. setTrackPosition(.., false) = no transition, so the
        // track does not sweep back from the old index (also what keeps this correct
        // under prefers-reduced-motion).
        totalSlides = list.length;
        slidesLoaded = new Set([active]);
        currentIndex = active;
        setTrackPosition(getBaseOffset(), false);   // getBaseOffset() re-measures trackWidth
        updateDots();
        preloadSlide(active + 1);
        preloadSlide(active - 1);
        // The thumb-strip arrow controller (inline in the section) only recomputes on
        // scroll/resize/load, and the rail just changed width underneath it.
        if (strip) {
            if (active === 0) strip.scrollLeft = 0;
            strip.dispatchEvent(new Event('scroll'));
        }
        return true;
    }

    // Pre-2026-08 behaviour: swap slide 0 only. Kept as the fallback path for any
    // page without per-colour data - notably ergonix.pl between the JS syncing and
    // the section being hand-ported (pl_adapt.py CONTENT_ADAPTED).
    function legacyMobileSlotZeroSwap(variantData) {
        var fi = variantData.featured_image;
        var fullUrl = fi.mobileSrc || fi.src;

        var slides = getSlides();
        if (slides[0]) {
            var img = slides[0].querySelector('.mobile-slider__image');
            if (img) {
                img.src = fullUrl;
                img.srcset = fullUrl + ' 600w';
                img.setAttribute('data-full-src', fullUrl);
                img.setAttribute('data-full-srcset', fullUrl + ' 600w');
            }
        }

        // Keep the thumb strip's colour slot in sync (slot 0 is the only colour
        // thumb - without this it keeps showing the previous colour's render)
        var firstThumbImg = document.querySelector('#mobileSliderThumbs .mobile-slider__thumb[data-index="0"] img');
        if (firstThumbImg && fi.mobileThumb) firstThumbImg.src = fi.mobileThumb;

        goToSlide(0);
    }

    function updateMobileImagesForVariant(color) {
        if (window.innerWidth > 768) return;

        var variantData = window.productVariantsData ? window.productVariantsData.find(function(v) { return v.option1 === color; }) : null;
        if (!variantData || !variantData.featured_image) return;

        // Pause any playing videos before the DOM under them changes
        getSlides().forEach(function(slide) {
            var video = slide.querySelector('.mobile-slider__video');
            if (video) video.pause();
        });

        var set = window.productMediaByColor && window.productMediaByColor[color];
        if (!set || !set.length) return legacyMobileSlotZeroSwap(variantData);

        var fi = variantData.featured_image;
        var list = [{
            type: 'image', src: fi.src, videoSrc: null, thumb: fi.thumb,
            mobileSrc: fi.mobileSrc, mobileThumb: fi.mobileThumb, alt: fi.alt || ''
        }].concat(set);

        // Same rule as desktop: hold the shopper's place across a colour change.
        var keep = Math.min(Math.max(currentIndex, 0), list.length - 1);
        if (!buildMobileRail(list, keep)) return legacyMobileSlotZeroSwap(variantData);
        window.allProductMedia = list;
    }


    function openMobileCart() {
        // Mobile cart functionality
        console.log('Mobile cart opened');
    }
    
    function initMobileCarousel() {
        // Legacy - now handled by initializeMobileGallery
        initializeMobileGallery();
    }

    function handleResponsiveSwitch() {
        if (window.innerWidth <= 768) {
            initializeMobileGallery();
        }
    }
    
    
    // Gallery-aware scroll behavior for header and announcement bar
    const initScrollBehavior = function(retryCount = 0) {
            let lastScrollY = 0;
            let ticking = false;
            let isGalleryVisible = false;
            let observerConnected = false;
            
            // iOS detection for momentum scrolling
            const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
            const scrollThreshold = isIOS ? 10 : 5; // Higher threshold for iOS
            
            // Use more flexible selectors that work with template suffixes
            const announcementBar = document.querySelector('[id^="mobileAnnouncementBar"]');
            const mobileHeader = document.querySelector('.mobile-header');
            
            console.log('ScrollBehavior: Looking for elements:', {
                announcementBar: !!announcementBar,
                mobileHeader: !!mobileHeader,
                windowWidth: window.innerWidth,
                retryCount: retryCount
            });
            
            if (!announcementBar || !mobileHeader) {
                // Limit retries to prevent infinite loop
                if (retryCount < 5) {
                    console.warn(`ScrollBehavior: Elements not found, retry ${retryCount + 1}/5 in 500ms...`);
                    setTimeout(() => initScrollBehavior(retryCount + 1), 500);
                    return;
                } else {
                    console.log('ScrollBehavior: Elements not found after 5 retries. This is normal for Ergonix pages - header scroll behavior disabled.');
                    return;
                }
            }
            
            console.log('ScrollBehavior: Elements found, initializing...');
            
            // Simple scroll handler - no complex observers needed
            const updateScrollDirection = () => {
                const currentScrollY = window.scrollY;
                
                console.log('ScrollBehavior: Scroll detected', { currentScrollY, lastScrollY });
                
                // Skip if scroll is below threshold
                if (Math.abs(currentScrollY - lastScrollY) < scrollThreshold) {
                    ticking = false;
                    return;
                }
                
                const isScrollingDown = currentScrollY > lastScrollY;
                const isScrollingUp = currentScrollY < lastScrollY;
                
                if (isScrollingDown && currentScrollY > 50) {
                    // Hide when scrolling down past 50px
                    console.log('ScrollBehavior: Hiding elements (scrolling down)');
                    announcementBar.classList.add('hidden');
                    mobileHeader.classList.add('hidden');
                } else if (isScrollingUp) {
                    // Show when scrolling up
                    console.log('ScrollBehavior: Showing elements (scrolling up)');
                    announcementBar.classList.remove('hidden');
                    mobileHeader.classList.remove('hidden');
                }
                
                lastScrollY = currentScrollY;
                ticking = false;
            };
            
            const onScroll = () => {
                if (!ticking) {
                    window.requestAnimationFrame(updateScrollDirection);
                    ticking = true;
                }
            };
            
            window.addEventListener('scroll', onScroll, { passive: true });
            console.log('ScrollBehavior: Scroll listener attached successfully');
        };
    
    // Preload all variant images for instant mobile switching
    function preloadAllMobileVariantImages() {
        if (!window.productVariantsData || window.innerWidth > 768) return;

        console.log('Preloading all mobile variant images for instant switching...');

        window.productVariantsData.forEach(function(variant) {
            if (variant.images && variant.images.length > 0) {
                variant.images.forEach(function(imageData) {
                    // The phone slider shows mobileSrc/mobileThumb, so warm those, not
                    // the 1600px desktop files. Videos carry no image src.
                    const full = imageData.mobileSrc || imageData.src;
                    const small = imageData.mobileThumb || imageData.thumb;
                    if (full) { new Image().src = full; }
                    if (small) { new Image().src = small; }
                });
                console.log(`Preloaded ${variant.images.length} mobile images for ${variant.option1} variant`);
            }
            // Variant colour renders are excluded from variant.images - preload
            // each variant's own featured image (mobile sizes) for instant swaps.
            if (variant.featured_image) {
                if (variant.featured_image.mobileSrc) { new Image().src = variant.featured_image.mobileSrc; }
                if (variant.featured_image.mobileThumb) { new Image().src = variant.featured_image.mobileThumb; }
            }
        });
    }

    // Initialize mobile functionality
    document.addEventListener('DOMContentLoaded', function() {
        // Initialize gallery
        if (window.innerWidth <= 768) {
            setTimeout(initializeMobileGallery, 100);
            // Initialize scroll behavior for mobile
            initScrollBehavior();

            // Initialize mobile gallery with correct variant images on page load
            setTimeout(function() {
                var targetColor = null;
                var urlVariant = null;
                // Check URL for variant ID and resolve to color
                var urlParams = new URLSearchParams(window.location.search);
                var variantFromUrl = urlParams.get('variant');
                console.log('[MobileInit] URL variant param:', variantFromUrl, '| productVariantsData count:', window.productVariantsData ? window.productVariantsData.length : 0);
                if (variantFromUrl && window.productVariantsData) {
                    // Try color name first
                    if (['Black', 'Grey', 'Gray'].includes(variantFromUrl)) {
                        targetColor = variantFromUrl === 'Gray' ? 'Grey' : variantFromUrl;
                        console.log('[MobileInit] Resolved color name directly:', targetColor);
                    } else {
                        // Variant ID - look up
                        urlVariant = window.productVariantsData.find(function(v) { return v.id === variantFromUrl; });
                        if (urlVariant) {
                            targetColor = urlVariant.option1;
                            console.log('[MobileInit] Resolved variant ID to color:', targetColor, '| variant:', urlVariant.title);
                        } else {
                            console.log('[MobileInit] Variant ID NOT FOUND in productVariantsData. IDs available:', window.productVariantsData.map(function(v) { return v.id; }));
                        }
                    }
                }
                if (!targetColor) {
                    // No URL variant param - HTML already renders the correct default image.
                    // Skip updateMobileImagesForVariant to prevent image flash/reload.
                    console.log('[MobileInit] No URL variant - skipping image update (HTML already correct)');
                }
                // Same rule as desktop: if Shopify already rendered this colour
                // server-side, rebuilding the rail would only repaint what is
                // already right, and that repaint is the flash we are removing.
                var serverColor = (document.querySelector('.ergonix-wrapper .mobile-color-option.active') || {}).dataset;
                serverColor = serverColor && serverColor.color;
                // Multi-option products (the desks: frame colour + top) cannot be matched
                // by option1 alone: "White" finds the first white-frame variant, whose top
                // may differ. If slide 0 already shows this exact variant's own photo,
                // the server got it right and the swap would show the wrong one.
                var fileOf = function(u) { return String(u || '').split('?')[0].split('/').pop(); };
                var firstImg = document.querySelector('.mobile-slider__slide:first-child .mobile-slider__image');
                var serverHasVariant = !!(urlVariant && urlVariant.featured_image && firstImg &&
                    fileOf(urlVariant.featured_image.mobileSrc || urlVariant.featured_image.src) === fileOf(firstImg.getAttribute('src')));
                if (targetColor && (targetColor === serverColor || serverHasVariant)) {
                    console.log('[MobileInit] URL variant matches the server-rendered image - nothing to do');
                } else if (targetColor) {
                    console.log('[MobileInit] Calling updateMobileImagesForVariant with:', targetColor);
                    updateMobileImagesForVariant(targetColor);
                }
                // Log final state
                var firstSlideImg = document.querySelector('.mobile-slider__slide:first-child .mobile-slider__image');
                if (firstSlideImg) console.log('[MobileInit] Final first slide src:', firstSlideImg.src);
            }, 200); // Small delay to ensure gallery is initialized

            // Safety net: ensure slider is at slide 0 with correct transform
            setTimeout(function() {
                var track = document.getElementById('mobileSliderTrack');
                if (track && currentIndex === 0) {
                    var currentTransform = track.style.transform;
                    if (currentTransform !== 'translateX(0px)') {
                        console.log('[MobileSafetyNet] Track position wrong:', currentTransform, '- correcting to translateX(0px)');
                        track.style.transform = 'translateX(0px)';
                    }
                }
            }, 500);

            // Preload all variant images for instant switching
            erxWhenSettled(preloadAllMobileVariantImages);
        }

        // Handle responsive switching on resize
        let resizeTimeout;
        window.addEventListener('resize', function() {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                if (window.innerWidth <= 768) {
                    initializeMobileGallery();
                }
            }, 250);
        });
    });
    
    // Modern Accordion functionality with accessibility
    document.addEventListener('DOMContentLoaded', function() {
      const accordionItems = document.querySelectorAll('.mobile-accordion-details .accordion-item');
      
      accordionItems.forEach(item => {
        const header = item.querySelector('.accordion-header');
        const content = item.querySelector('.accordion-content');
        const icon = item.querySelector('.accordion-icon');
        // A closed panel is aria-hidden, so its links must not take keyboard focus either.
        if (content) content.inert = content.getAttribute('aria-hidden') === 'true';
        
        header.addEventListener('click', function() {
          const isExpanded = item.classList.contains('expanded');
          
          if (isExpanded) {
            // Collapse. The panel height comes from window.ErgonixCollapse (top of this
            // file), which measures the content instead of animating towards a fixed cap.
            item.classList.remove('expanded');
            window.ErgonixCollapse.close(content, 'expanded');
            icon.classList.remove('minus');
            icon.classList.add('plus');
            
            // Update ARIA attributes
            header.setAttribute('aria-expanded', 'false');
            content.setAttribute('aria-hidden', 'true');
            content.inert = true;
          } else {
            // Expand
            item.classList.add('expanded');
            window.ErgonixCollapse.open(content, 'expanded');
            icon.classList.remove('plus');
            icon.classList.add('minus');
            
            // Update ARIA attributes
            header.setAttribute('aria-expanded', 'true');
            content.setAttribute('aria-hidden', 'false');
            content.inert = false;
          }
        });
        
        // Keyboard support
        header.addEventListener('keydown', function(e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            header.click();
          }
        });
      });
    });
    // ============================================
    // MOBILE STICKY ADD TO CART FUNCTIONALITY
    // ============================================

    // Initialize sticky cart functionality
    function initStickyCart() {
        const stickyCart = document.getElementById('mobile-sticky-cart');

        // Move sticky cart to document.body to avoid position:fixed issues
        // caused by parent transforms/will-change/contain from app embeds or theme CSS
        if (stickyCart && stickyCart.parentElement !== document.body) {
            document.body.appendChild(stickyCart);
        }

        // Device-specific button selection
        let orderBtn;
        if (window.innerWidth <= 768) {
            // Mobile: observe the mobile order button
            orderBtn = document.querySelector('.mobile-order-button');
            console.log('Mobile mode: looking for .mobile-order-button');
        } else {
            // Desktop fallback: observe desktop button (though sticky cart is mobile-only)
            orderBtn = document.querySelector('.order-btn');
            console.log('Desktop mode: looking for .order-btn');
        }

        // Also observe the price section
        const priceSection = document.querySelector('.mobile-price-wrapper');

        if (!stickyCart || !orderBtn) {
            console.warn('Sticky cart init failed:', {
                stickyCart: !!stickyCart,
                orderBtn: !!orderBtn,
                priceSection: !!priceSection,
                orderBtnClass: orderBtn ? orderBtn.className : 'not found',
                windowWidth: window.innerWidth
            });
            return;
        }

        console.log('Initializing sticky cart...', {
            stickyCart,
            orderBtn,
            priceSection,
            buttonClass: orderBtn.className,
            windowWidth: window.innerWidth
        });

        // Track visibility state of both elements
        let orderBtnVisible = true;
        let priceSectionVisible = true;

        // Function to update sticky cart visibility based on both elements
        function updateStickyCartVisibility() {
            // Show sticky cart only when BOTH order button AND price section are out of view
            if (!orderBtnVisible && !priceSectionVisible) {
                stickyCart.classList.add('visible');
                console.log('Both order button and price out of view → showing sticky cart');
            } else {
                stickyCart.classList.remove('visible');
                console.log('Order button or price in view → hiding sticky cart', {
                    orderBtnVisible,
                    priceSectionVisible
                });
            }
        }

        // Observer for order button
        const orderBtnObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                // Below the screen means the visitor has not reached the button yet.
                // isIntersecting is false there too, which slid the bar over the title
                // and price the moment the page loaded.
                const rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
                orderBtnVisible = entry.isIntersecting || entry.boundingClientRect.top > rootTop;
                updateStickyCartVisibility();
            });
        }, {
            threshold: 0,  // Trigger as soon as any part enters/leaves viewport
            rootMargin: '0px 0px -50px 0px'  // 50px buffer from bottom of viewport
        });

        orderBtnObserver.observe(orderBtn);
        console.log('IntersectionObserver attached to order button:', orderBtn.className);

        // Observer for price section (if it exists)
        if (priceSection) {
            const priceSectionObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    priceSectionVisible = entry.isIntersecting;
                    updateStickyCartVisibility();
                });
            }, {
                threshold: 0,
                rootMargin: '0px 0px -50px 0px'
            });

            priceSectionObserver.observe(priceSection);
            console.log('IntersectionObserver attached to price section');
        }
        
        // Initialize sticky cart prices on load
        setTimeout(updateStickyCartPrice, 100);
        console.log('Initializing sticky cart prices...');

        // Visual Viewport API: Keep sticky cart anchored to visual viewport bottom
        // Only adjust when virtual keyboard is open (large offset), not for browser chrome
        if (window.visualViewport) {
            const adjustStickyCartPosition = () => {
                const offset = window.innerHeight - window.visualViewport.height;
                // Only adjust for significant offsets (virtual keyboard > 150px)
                // Browser address bar changes are small (~50-80px) and cause jitter
                if (offset > 150) {
                    stickyCart.style.bottom = offset + 'px';
                } else {
                    stickyCart.style.bottom = '0px';
                }
            };

            window.visualViewport.addEventListener('resize', adjustStickyCartPosition);
            adjustStickyCartPosition();
        }
        
        // Handle sticky cart color selection
        const stickyColorDots = document.querySelectorAll('.sticky-color-selector .color-dot');
        stickyColorDots.forEach(dot => {
            dot.addEventListener('click', function(e) {
                e.preventDefault();
                
                const color = this.dataset.color;
                console.log('Sticky color dot clicked:', color);
                
                // Update active state in sticky cart
                stickyColorDots.forEach(d => d.classList.remove('active'));
                this.classList.add('active');
                
                // Call the same function as main mobile color selector
                if (window.mobileSelectColor) {
                    window.mobileSelectColor(color);
                    console.log('Called mobileSelectColor with:', color);
                } else {
                    console.error('mobileSelectColor function not available');
                }
            });
        });
        
        // Sync sticky cart with main color selector changes
        const mainColorBtns = document.querySelectorAll('.mobile-color-option');
        mainColorBtns.forEach(btn => {
            btn.addEventListener('click', function() {
                const color = this.dataset.color;
                console.log('Main color button clicked, syncing sticky cart:', color);
                
                // Update sticky cart active state
                stickyColorDots.forEach(dot => {
                    dot.classList.remove('active');
                    if (dot.dataset.color === color) {
                        dot.classList.add('active');
                    }
                });
                
                // Update price
                setTimeout(updateStickyCartPrice, 100); // Small delay to let main script update
            });
        });
        
        // Handle sticky order button click
        // Bound ONCE. This initialiser re-runs from DOMContentLoaded, load and every resize, and
        // each run used to add another click listener, so one tap could add several units. (The
        // markup's own onclick fires in the same tick; cart.js dedupes that pair on its side.)
        const stickyOrderBtn = document.getElementById('sticky-order-btn');
        if (stickyOrderBtn && stickyOrderBtn.dataset.cartBound !== '1') {
            stickyOrderBtn.dataset.cartBound = '1';
            stickyOrderBtn.addEventListener('click', function(e) {
                e.preventDefault();
                console.log('Sticky order button clicked');
                
                // Use the same add to cart function as main button
                if (window.cart && window.cart.addToCart) {
                    window.cart.addToCart();
                } else {
                    // Fallback: trigger main order button
                    const mainOrderBtn = document.querySelector('.order-btn');
                    if (mainOrderBtn) {
                        mainOrderBtn.click();
                    }
                }
            });
        }
    }

    // Update sticky cart price based on current selection
    function updateStickyCartPrice() {
        const currentPriceEl = document.getElementById('sticky-current-price');
        const originalPriceEl = document.getElementById('sticky-original-price');
        const discountBadgeEl = document.getElementById('sticky-discount-badge');
        
        // Get prices from MOBILE product area (since sticky cart is mobile-only)
        const mainCurrentPrice = document.getElementById('mobile-price-current');
        const mainOriginalPrice = document.getElementById('mobile-price-original');
        
        console.log('UpdateStickyCartPrice - Found elements:', {
            currentPriceEl: !!currentPriceEl,
            originalPriceEl: !!originalPriceEl,
            mainCurrentPrice: !!mainCurrentPrice,
            mainOriginalPrice: !!mainOriginalPrice,
            mainCurrentText: mainCurrentPrice ? mainCurrentPrice.textContent : 'N/A',
            mainOriginalText: mainOriginalPrice ? mainOriginalPrice.textContent : 'N/A'
        });
        
        if (currentPriceEl && mainCurrentPrice) {
            currentPriceEl.textContent = mainCurrentPrice.textContent;
            console.log('Updated sticky cart current price:', mainCurrentPrice.textContent);
        }
        
        if (originalPriceEl && mainOriginalPrice) {
            originalPriceEl.textContent = mainOriginalPrice.textContent;
            console.log('Updated sticky cart original price:', mainOriginalPrice.textContent);
        }
        
        // Calculate discount percentage (if both prices available)
        if (mainCurrentPrice && mainOriginalPrice && discountBadgeEl) {
            const current = parseFloat(mainCurrentPrice.textContent.replace(/[€$,]/g, ''));
            const original = parseFloat(mainOriginalPrice.textContent.replace(/[€$,]/g, ''));
            
            console.log('Price calculation:', { current, original });
            
            if (current && original && current < original) {
                const discount = Math.round(((original - current) / original) * 100);
                discountBadgeEl.textContent = `-${discount}%`;
                console.log('Updated discount badge:', `-${discount}%`);
            }
        }
    }
    
    // ============================================
    // DESKTOP STICKY ADD TO CART FUNCTIONALITY
    // ============================================
    
    // Initialize desktop sticky cart functionality
    /**
     * Bring the desktop sticky bar in line with the selected colour: active dot,
     * colour name, thumbnail and price.
     *
     * The thumbnail was never written by anything, so the bar kept showing a black
     * chair next to a grey price. The active dot and name were maintained by a
     * separate listener that guessed the colour from the swatch's computed
     * background (`bgStyle.includes('196')`); the swatch is #9E9E9E =
     * rgb(158,158,158), so that test never matched and every click on the main
     * selector reported Black. Never key on a rendered colour value - the same rule
     * as ergonix-swatch-style.liquid and ergonix-color-token.liquid.
     */
    function syncDesktopStickyCart(color) {
        document.querySelectorAll('.desktop-sticky-color-selector .color-dot')
            .forEach(dot => dot.classList.toggle('active', dot.dataset.color === color));

        const nameEl = document.getElementById('desktop-sticky-color-name');
        if (nameEl) {
            const cs = window.cartStrings || {};
            const map = { Grey: cs.colorGrey, Gray: cs.colorGrey, Black: cs.colorBlack };
            // option1 is already localised (Juoda / Czarny / Melns / Must / Cerna),
            // so falling back to it keeps this correct on every market.
            nameEl.textContent = map[color] || color;
        }

        const thumb = document.getElementById('desktop-sticky-thumb');
        const vd = (window.productVariantsData || []).find(v => v.option1 === color);
        if (thumb && vd && vd.featured_image) {
            const fi = vd.featured_image;
            const next = fi.thumb || fi.mobileThumb || fi.mobileSrc || fi.src;
            // The sticky thumb carries a 64/128 srcset; keep it in step or the old colour stays.
            if (thumb.hasAttribute('srcset')) {
                if (next && /[?&]width=\d+/.test(next)) thumb.srcset = erxWidth(next, 64) + ' 1x, ' + erxWidth(next, 128) + ' 2x';
                else thumb.removeAttribute('srcset');
            }
            thumb.src = next && /[?&]width=\d+/.test(next) ? erxWidth(next, 64) : next;
        }

        if (typeof updateDesktopStickyCartPrice === 'function') {
            setTimeout(updateDesktopStickyCartPrice, 120);
        }
    }
    window.ergonixSyncStickyCart = syncDesktopStickyCart;

    function initDesktopStickyCart() {
        const stickyCart = document.getElementById('desktop-sticky-cart');

        // Move sticky cart to document.body to avoid position:fixed issues
        if (stickyCart && stickyCart.parentElement !== document.body) {
            document.body.appendChild(stickyCart);
        }

        const orderBtn = document.querySelector('.desktop-order-btn, .order-btn');

        if (!stickyCart || !orderBtn) return;

        // Wire the listeners once. This runs from DOMContentLoaded, from load, AND now
        // from every resize that crosses above 768, so without the guard each swatch
        // click would fire desktopSelectColor several times and rebuild the gallery
        // rail once per duplicated listener. The guard is set only once both the bar
        // and the CTA exist, so an early call that bailed out can still retry later.
        if (stickyCart.dataset.desktopStickyInit === '1') return;
        stickyCart.dataset.desktopStickyInit = '1';

        console.log('Initializing desktop sticky cart...', { stickyCart, orderBtn });
        
        // Show sticky cart when main order button is out of view
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                // Only once the button has been scrolled past, not while it is still
                // below the screen (a tall hero put it there at page load).
                const rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
                if (!entry.isIntersecting && entry.boundingClientRect.top <= rootTop) {
                    // Main order button has been scrolled past, show sticky cart
                    stickyCart.classList.add('visible');
                    console.log('Main order button out of view, showing desktop sticky cart');
                } else {
                    // Main order button is visible, hide sticky cart
                    stickyCart.classList.remove('visible');
                    console.log('Main order button visible, hiding desktop sticky cart');
                }
            });
        }, { 
            threshold: 0,
            rootMargin: '0px 0px -50px 0px' // Show sticky cart when order button is 50px from bottom
        });
        
        observer.observe(orderBtn);
        
        // Initialize sticky cart prices on load
        setTimeout(updateDesktopStickyCartPrice, 100);
        console.log('Initializing desktop sticky cart prices...');
        
        // Handle sticky cart color selection. The dot only drives the page; the page's
        // selectColor() then calls syncDesktopStickyCart(), which owns the active dot,
        // the colour name and the thumbnail. Keeping a second copy of that bookkeeping
        // here is what let the bar drift out of step with the page.
        const stickyColorDots = document.querySelectorAll('.desktop-sticky-color-selector .color-dot');
        stickyColorDots.forEach(dot => {
            dot.addEventListener('click', function(e) {
                e.preventDefault();
                const color = this.dataset.color;
                if (window.desktopSelectColor) {
                    window.desktopSelectColor(color);
                } else if (window.ErgonixProduct && window.ErgonixProduct.selectColor) {
                    window.ErgonixProduct.selectColor(color);
                }
            });
        });

        // Reflect whatever colour is already selected when the bar first initialises.
        const initialSwatch = document.querySelector('.ergonix-wrapper .color-option.active');
        if (initialSwatch && initialSwatch.dataset.color) syncDesktopStickyCart(initialSwatch.dataset.color);


        // Handle sticky checkout button click
        const stickyCheckoutBtn = document.getElementById('desktop-sticky-checkout-btn');
        if (stickyCheckoutBtn && stickyCheckoutBtn.dataset.cartBound !== '1') {
            stickyCheckoutBtn.dataset.cartBound = '1';
            stickyCheckoutBtn.addEventListener('click', function(e) {
                e.preventDefault();
                console.log('Desktop sticky checkout button clicked');
                
                // Use the same add to cart function as main button
                if (window.cart && window.cart.addToCart) {
                    window.cart.addToCart();
                } else {
                    // Fallback: trigger main order button
                    const mainOrderBtn = document.querySelector('.order-btn, .desktop-order-btn');
                    if (mainOrderBtn) {
                        mainOrderBtn.click();
                    }
                }
            });
        }
    }
    
    // Update desktop sticky cart price based on current selection
    function updateDesktopStickyCartPrice() {
        const currentPriceEl = document.getElementById('desktop-sticky-current-price');
        const originalPriceEl = document.getElementById('desktop-sticky-original-price');
        const discountBadgeEl = document.getElementById('desktop-sticky-discount-badge');
        
        // Get prices from DESKTOP product area
        const mainCurrentPrice = document.getElementById('desktop-current-price');
        const mainOriginalPrice = document.getElementById('desktop-original-price');
        
        console.log('UpdateDesktopStickyCartPrice - Found elements:', {
            currentPriceEl: !!currentPriceEl,
            originalPriceEl: !!originalPriceEl,
            mainCurrentPrice: !!mainCurrentPrice,
            mainOriginalPrice: !!mainOriginalPrice,
            mainCurrentText: mainCurrentPrice ? mainCurrentPrice.textContent : 'N/A',
            mainOriginalText: mainOriginalPrice ? mainOriginalPrice.textContent : 'N/A'
        });
        
        if (currentPriceEl && mainCurrentPrice) {
            currentPriceEl.textContent = mainCurrentPrice.textContent;
            console.log('Updated desktop sticky cart current price:', mainCurrentPrice.textContent);
        }

        if (originalPriceEl && mainOriginalPrice) {
            originalPriceEl.textContent = mainOriginalPrice.textContent;
            console.log('Updated desktop sticky cart original price:', mainOriginalPrice.textContent);
        }

        // Calculate discount percentage (if both prices available)
        if (mainCurrentPrice && mainOriginalPrice && discountBadgeEl) {
            const current = parseFloat(mainCurrentPrice.textContent.replace(/[^\d.,]/g, '').replace(',', '.'));
            const original = parseFloat(mainOriginalPrice.textContent.replace(/[^\d.,]/g, '').replace(',', '.'));
            
            console.log('Desktop price calculation:', { current, original });
            
            if (current && original && current < original) {
                const discount = Math.round(((original - current) / original) * 100);
                discountBadgeEl.textContent = `-${discount}%`;
                console.log('Updated desktop sticky discount badge:', `-${discount}%`);
            }
        }
    }
    
    // Initialize sticky carts when DOM is ready
    document.addEventListener('DOMContentLoaded', function() {
        // Small delay to ensure all elements are loaded
        if (window.innerWidth <= 768) {
            // Mobile only
            setTimeout(initStickyCart, 500);
        } else {
            // Desktop only
            setTimeout(initDesktopStickyCart, 500);
        }
    });
    
    // Also initialize on window load as backup
    window.addEventListener('load', function() {
        if (window.innerWidth <= 768) {
            // Mobile only
            setTimeout(initStickyCart, 100);
        } else {
            // Desktop only
            setTimeout(initDesktopStickyCart, 100);
        }
    });

    // Re-initialize on resize/orientation change. BOTH directions: this used to run
    // only for widths <= 768, so a page that loaded narrow and was then widened (an
    // iPad turned to landscape, a desktop window dragged wider, responsive dev tools)
    // never ran initDesktopStickyCart at all. The desktop bar was therefore never
    // moved to <body>, never got its IntersectionObserver and never got the class that
    // shows it: no sticky add-to-cart for that whole session (found 2026-08-12).
    let stickyCartResizeTimer;
    window.addEventListener('resize', function() {
        clearTimeout(stickyCartResizeTimer);
        stickyCartResizeTimer = setTimeout(() => {
            if (window.innerWidth <= 768) {
                initStickyCart();
            } else {
                initDesktopStickyCart();
            }
        }, 250);
    });

    // ============================================
    // DESKTOP GALLERY STICKY SCROLLING
    // ============================================
    
    function initDesktopGallerySticky() {
        // Only run in the TRUE side-by-side gallery layout (>=993px). The CSS
        // stacks the gallery into a column at <=992px with NO sticky - forcing
        // inline position:sticky here fought that and glitched on scroll.
        if (window.innerWidth <= 992) return;

        const thumbnailsSection = document.querySelector('.ergonix-wrapper .thumbnails-section');
        const mainImageSection = document.querySelector('.ergonix-wrapper .main-image-section');
        const infoCard = document.querySelector('.ergonix-wrapper .info-card');

        if (!thumbnailsSection || !mainImageSection || !infoCard) return;

        console.log('Initializing desktop gallery sticky scrolling...');

        let stickyActive = true;
        // Function-scope: the init setTimeout below uses it too. It used to be
        // declared inside updateStickyBehavior(), so the init crashed with
        // "headerHeight is not defined" on every desktop PDP load.
        const headerHeight = 134;

        // Calculate when to stop sticky behavior
        function updateStickyBehavior() {
            const infoCardRect = infoCard.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            
            // Calculate the bottom of info-card relative to viewport
            const infoCardBottom = infoCardRect.bottom;
            
            // If info-card bottom is at header height, stop sticky
            const shouldStopSticky = infoCardBottom <= (headerHeight + 50); // 50px buffer
            
            if (shouldStopSticky && stickyActive) {
                // Add transitioning class for smooth animation
                thumbnailsSection.classList.add('transitioning');
                mainImageSection.classList.add('transitioning');
                
                // Stop sticky behavior - make sections follow normal flow
                setTimeout(() => {
                    thumbnailsSection.style.position = 'relative';
                    thumbnailsSection.style.top = 'auto';
                    mainImageSection.style.position = 'relative';
                    mainImageSection.style.top = 'auto';
                    stickyActive = false;
                    console.log('Stopped sticky at info-card bottom');
                    
                    // Remove transitioning class after animation
                    setTimeout(() => {
                        thumbnailsSection.classList.remove('transitioning');
                        mainImageSection.classList.remove('transitioning');
                    }, 500);
                }, 50);
            } else if (!shouldStopSticky && !stickyActive) {
                // Add transitioning class for smooth animation
                thumbnailsSection.classList.add('transitioning');
                mainImageSection.classList.add('transitioning');
                
                // Resume sticky behavior
                setTimeout(() => {
                    thumbnailsSection.style.position = 'sticky';
                    thumbnailsSection.style.top = headerHeight + 'px';
                    mainImageSection.style.position = 'sticky';
                    mainImageSection.style.top = headerHeight + 'px';
                    stickyActive = true;
                    console.log('Resumed sticky behavior');
                    
                    // Remove transitioning class after animation
                    setTimeout(() => {
                        thumbnailsSection.classList.remove('transitioning');
                        mainImageSection.classList.remove('transitioning');
                    }, 500);
                }, 50);
            }
        }
        
        // Listen for scroll events
        let ticking = false;
        function handleScroll() {
            if (!ticking) {
                requestAnimationFrame(function() {
                    updateStickyBehavior();
                    ticking = false;
                });
                ticking = true;
            }
        }
        
        window.addEventListener('scroll', handleScroll);
        
        // Initial check and force sticky positioning
        setTimeout(() => {
            // Explicitly set sticky positioning to override any CSS conflicts
            thumbnailsSection.style.position = 'sticky';
            thumbnailsSection.style.top = headerHeight + 'px';
            mainImageSection.style.position = 'sticky';
            mainImageSection.style.top = headerHeight + 'px';
            
            updateStickyBehavior();
            console.log('Forced sticky positioning on initialization');
        }, 100);
        
        // Handle window resize
        window.addEventListener('resize', function() {
            if (window.innerWidth <= 992) {
                // Disable in the stacked (mobile/tablet) layout - clear the
                // inline overrides so the CSS column layout wins.
                thumbnailsSection.style.position = '';
                thumbnailsSection.style.top = '';
                mainImageSection.style.position = '';
                mainImageSection.style.top = '';
                window.removeEventListener('scroll', handleScroll);
            } else {
                // Re-enable on desktop
                setTimeout(updateStickyBehavior, 100);
            }
        });
    }

    // Initialize gallery sticky scrolling
    document.addEventListener('DOMContentLoaded', function() {
        if (window.innerWidth > 992) {
            setTimeout(initDesktopGallerySticky, 600); // After sticky cart init
        }
    });

    // Also initialize on window load as backup
    window.addEventListener('load', function() {
        if (window.innerWidth > 992) {
            setTimeout(initDesktopGallerySticky, 200);
        }
    });
    
    // Expose mobile functions globally so HTML can call them
    window.mobileSelectColor = mobileSelectColor;
    
    // ============================================
    // DYNAMIC TIMELINE DATE FUNCTIONALITY
    // ============================================
    
    // Function to update timeline dates dynamically
    function updateTimelineDates() {
        // Update desktop timeline
        const desktopTimelineItems = document.querySelectorAll('.desktop-timeline .timeline-item');
        updateTimelineSection(desktopTimelineItems, 'desktop');

        // Update mobile timeline
        const mobileTimelineItems = document.querySelectorAll('.mobile-timeline .timeline-item');
        updateTimelineSection(mobileTimelineItems, 'mobile');
    }
    
    // Helper function to update a timeline section
    // Only sets the "order placed" date (index 0). Ship/delivery dates
    // are variant-aware and owned by updateTimelineForVariant.
    function updateTimelineSection(timelineItems, sectionType) {
        if (timelineItems.length === 0) return;

        const today = new Date();
        const firstDateElement = timelineItems[0] && timelineItems[0].querySelector('.timeline-date');
        if (firstDateElement) firstDateElement.textContent = formatTimelineDate(today, 0);
    }
    
    // Format a single date for timeline display
    function formatTimelineDate(date, daysOffset) {
        const targetDate = new Date(date);
        targetDate.setDate(targetDate.getDate() + daysOffset);

        // Order is locale-dependent: "Rugs. 9" in LT, "9. sept." in LV/EE.
        const months = (window.cartStrings && window.cartStrings.months) || ['Saus.', 'Vas.', 'Kov.', 'Bal.', 'Geg.', 'Birž.', 'Liep.', 'Rugp.', 'Rugs.', 'Spal.', 'Lapkr.', 'Gruod.'];
        return ergxFormatShortDate(months, targetDate);
    }
    
    // Format a date range for timeline display
    function formatTimelineDateRange(date, startDaysOffset, endDaysOffset) {
        const startDate = new Date(date);
        const endDate = new Date(date);

        startDate.setDate(startDate.getDate() + startDaysOffset);
        endDate.setDate(endDate.getDate() + endDaysOffset);

        // Order is locale-dependent: "Spal. 5-6" in LT, "5.-6. okt." in LV/EE.
        const months = (window.cartStrings && window.cartStrings.months) || ['Saus.', 'Vas.', 'Kov.', 'Bal.', 'Geg.', 'Birž.', 'Liep.', 'Rugp.', 'Rugs.', 'Spal.', 'Lapkr.', 'Gruod.'];
        return ergxFormatShortDateRange(months, startDate, endDate);
    }
    
    // Get ordinal suffix for day (1st, 2nd, 3rd, 4th, etc.)
    function getOrdinalSuffix(day) {
        if (day >= 11 && day <= 13) {
            return 'th';
        }
        switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    }
    
    // Get localized short month name
    function getShortMonthName(monthIndex) {
        const months = (window.cartStrings && window.cartStrings.months) || ['Saus.', 'Vas.', 'Kov.', 'Bal.', 'Geg.', 'Birž.', 'Liep.', 'Rugp.', 'Rugs.', 'Spal.', 'Lapkr.', 'Gruod.'];
        return months[monthIndex];
    }
    
    // Initialize timeline dates on page load
    document.addEventListener('DOMContentLoaded', function() {
        setTimeout(function () {
            updateTimelineDates();
            // Apply variant-aware ship/delivery dates using the current URL variant
            if (window.updateTimelineForVariant) {
                const variantFromUrl = new URLSearchParams(window.location.search).get('variant');
                const normalized = variantFromUrl === 'Gray' ? 'Grey' : variantFromUrl;
                window.updateTimelineForVariant(normalized || 'Grey');
            }
        }, 100);
    });
    
    // Expose updateTimelineDates globally for manual updates if needed
    window.updateTimelineDates = updateTimelineDates;

    // Mobile Video Functions
    function playVideo(videoElement) {
        const overlay = document.getElementById('mobileVideoPlayOverlay');
        if (videoElement && overlay) {
            videoElement.play();
            overlay.classList.add('hidden');
        }
    }

    function pauseVideo(videoElement) {
        const overlay = document.getElementById('mobileVideoPlayOverlay');
        if (videoElement && overlay) {
            videoElement.pause();
            overlay.classList.remove('hidden');
        }
    }

    // Awkward Sitting Video Functions
    function playAwkwardVideo(videoElement) {
        const overlay = document.getElementById('mobileAwkwardPlayOverlay');
        if (videoElement && overlay) {
            videoElement.play();
            overlay.classList.add('hidden');
        }
    }

    function pauseAwkwardVideo(videoElement) {
        const overlay = document.getElementById('mobileAwkwardPlayOverlay');
        if (videoElement && overlay) {
            videoElement.pause();
            overlay.classList.remove('hidden');
        }
    }

    // Armrest carousel: a native scroll-snap rail (CSS in sections/ergonix-product-features.liquid).
    // The track scrolls and the browser snaps; this only mirrors the rail's position into the
    // dots, the arrows and which video plays. It replaced hand-rolled touch handlers that
    // translated the track from window.innerWidth math, fought a CSS transition on every
    // touchmove, and let a slide leave its frame at either end.
    let armrestCurrentIndex = 0;

    function armrestTrack() { return document.getElementById('armrestTrack'); }
    function armrestSlides() {
        const track = armrestTrack();
        return track ? Array.from(track.querySelectorAll('.armrest-video-slide')) : [];
    }

    function initArmrestCarousel() {
        const track = armrestTrack();
        if (!track) return;
        let ticking = false;
        track.addEventListener('scroll', () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => { ticking = false; syncArmrestIndex(); });
        }, { passive: true });
        updateArmrestButtons();
        playCurrentArmrestVideo();
    }

    function syncArmrestIndex() {
        const track = armrestTrack();
        const slides = armrestSlides();
        if (!track || !slides.length) return;
        let best = 0, bestDist = Infinity;
        slides.forEach((slide, i) => {
            const d = Math.abs(slide.offsetLeft - track.scrollLeft);
            if (d < bestDist) { bestDist = d; best = i; }
        });
        if (best === armrestCurrentIndex) return;
        armrestCurrentIndex = best;
        updateArmrestDots();
        updateArmrestButtons();
        playCurrentArmrestVideo();
    }

    function scrollArmrestTo(index) {
        const track = armrestTrack();
        const slides = armrestSlides();
        if (!track || !slides[index]) return;
        const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        track.scrollTo({ left: slides[index].offsetLeft, behavior: reduced ? 'auto' : 'smooth' });
    }

    function nextArmrestVideo() {
        if (armrestCurrentIndex < armrestSlides().length - 1) scrollArmrestTo(armrestCurrentIndex + 1);
    }

    function prevArmrestVideo() {
        if (armrestCurrentIndex > 0) scrollArmrestTo(armrestCurrentIndex - 1);
    }

    function goToArmrestVideo(index) {
        if (index !== armrestCurrentIndex) scrollArmrestTo(index);
    }

    function updateArmrestDots() {
        document.querySelectorAll('.armrest-dot').forEach((dot, i) => {
            dot.classList.toggle('active', i === armrestCurrentIndex);
        });
    }

    function updateArmrestButtons() {
        const prevBtn = document.getElementById('armrestPrevBtn');
        const nextBtn = document.getElementById('armrestNextBtn');
        if (!prevBtn || !nextBtn) return;
        prevBtn.style.display = armrestCurrentIndex > 0 ? 'flex' : 'none';
        nextBtn.style.display = armrestCurrentIndex < armrestSlides().length - 1 ? 'flex' : 'none';
    }

    function playCurrentArmrestVideo() {
        // Pause all armrest videos IN THE CAROUSEL ONLY
        document.querySelectorAll('#armrestCarousel .armrest-video').forEach((video, index) => {
            if (index === armrestCurrentIndex) {
                video.currentTime = 0; // Reset to beginning
                video.play().catch(() => {
                    // Auto-play failed, retry after a short delay
                    setTimeout(() => {
                        video.play().catch(() => {
                            console.log('Auto-play prevented for armrest video', index);
                        });
                    }, 100);
                });
            } else {
                video.pause();
            }
        });
    }

    // Handle video play/pause events
    document.addEventListener('DOMContentLoaded', function() {
        // Initialize armrest carousel
        initArmrestCarousel();
        
        // Made to Fit You video
        const video = document.querySelector('.mobile-product-video');
        const overlay = document.getElementById('mobileVideoPlayOverlay');
        
        if (video && overlay) {
            // Show overlay when video is paused or ended
            video.addEventListener('pause', function() {
                overlay.classList.remove('hidden');
            });
            
            video.addEventListener('ended', function() {
                overlay.classList.remove('hidden');
            });
            
            // Hide overlay when video starts playing
            video.addEventListener('play', function() {
                overlay.classList.add('hidden');
            });
            
            // Handle overlay click
            overlay.addEventListener('click', function() {
                playVideo(video);
            });
        }

        // Awkward Sitting video
        const awkwardVideo = document.querySelector('.mobile-awkward-video');
        const awkwardOverlay = document.getElementById('mobileAwkwardPlayOverlay');
        
        if (awkwardVideo && awkwardOverlay) {
            // Show overlay when video is paused or ended
            awkwardVideo.addEventListener('pause', function() {
                awkwardOverlay.classList.remove('hidden');
            });
            
            awkwardVideo.addEventListener('ended', function() {
                awkwardOverlay.classList.remove('hidden');
            });
            
            // Hide overlay when video starts playing
            awkwardVideo.addEventListener('play', function() {
                awkwardOverlay.classList.add('hidden');
            });
            
            // Handle overlay click
            awkwardOverlay.addEventListener('click', function() {
                playAwkwardVideo(awkwardVideo);
            });
        }

        // Initial OOS state for mobile chair (when default color is sold out)
        var activeMobileSwatch = document.querySelector('.ergonix-wrapper .mobile-color-option.active');
        if (activeMobileSwatch && window.ergonixApplyOOS) {
            var mColor = activeMobileSwatch.dataset.color;
            var mVariantId = activeMobileSwatch.dataset.variantId || (window.productVariants && window.productVariants[mColor]);
            window.ergonixApplyOOS(mColor, mVariantId, '#oos-chair-mobile', '#ergonix-chair-mobile-cta');
        }
    });

    // Expose mobile gallery functions for button onclick handlers
    // Fast clicks on a gallery arrow count as double/triple clicks, and the browser
    // then selects the nearest text (the promo banner). Block that selection only.
    document.addEventListener('mousedown', function(e) {
        if (e.detail > 1 && e.target.closest && e.target.closest('.image-nav, .mobile-slider__nav')) e.preventDefault();
    });

    window.ErgonixMobile = {
        nextImage: nextImage,
        previousImage: previousImage,
        selectImage: selectImage,
        selectMedia: selectMedia,
        changeImage: selectImage,
        goToSlide: goToSlide,
        toggleMute: mobileToggleMute,
        playVideo: playVideo,
        pauseVideo: pauseVideo,
        playAwkwardVideo: playAwkwardVideo,
        pauseAwkwardVideo: pauseAwkwardVideo,
        nextArmrestVideo: nextArmrestVideo,
        prevArmrestVideo: prevArmrestVideo,
        goToArmrestVideo: goToArmrestVideo
    };
})();